import { lazy, Suspense } from "react";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/shared/Seo";
import HeroSection from "@/components/home/HeroSection";
import ScrollReveal from "@/components/shared/ScrollReveal";

// Abaixo da dobra: lazy para reduzir o JS do primeiro carregamento (embla + framer-motion).
const MissionVisionSection = lazy(() => import("@/components/home/MissionVisionSection"));
const ServicesSection = lazy(() => import("@/components/home/ServicesSection"));
const B2BSolutionsSection = lazy(() => import("@/components/empresas/B2BSolutionsSection"));
const ClinicGallerySection = lazy(() => import("@/components/home/ClinicGallerySection"));
const SocialSection = lazy(() => import("@/components/home/SocialSection"));

const SectionFallback = () => <div className="min-h-[400px]" />;

const Index = () => {
  return (
    <Layout>
      <Seo
        title="Saúde Mental Corporativa SP | Equilybra Company"
        description="Consultoria B2B em saúde mental corporativa em SP: NR-1, laudos psicológicos, psicólogo in company, palestras e treinamentos de RH."
        path="/"
        keywords="saúde mental corporativa São Paulo, laudos psicológicos para empresas, NR-1, psicólogo in company, palestras corporativas, treinamento de lideranças, riscos psicossociais"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Serviços Corporativos Equilybra Company",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Saúde Mental Corporativa e NR-1", url: "https://www.equilybracompany.com.br/servicos/empresas/saude-mental-nr1" },
              { "@type": "ListItem", position: 2, name: "Laudos Psicológicos para Empresas", url: "https://www.equilybracompany.com.br/servicos/empresas/laudos-psicologicos" },
              { "@type": "ListItem", position: 3, name: "Psicólogo In Company", url: "https://www.equilybracompany.com.br/servicos/empresas/terapia-empresas" },
              { "@type": "ListItem", position: 4, name: "Atestado Psicossocial NR-33/NR-35", url: "https://www.equilybracompany.com.br/servicos/empresas/atestado-psicosocial" },
              { "@type": "ListItem", position: 5, name: "Perícia Psicológica", url: "https://www.equilybracompany.com.br/servicos/pericia-psicologica" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Minha empresa é obrigada a cumprir a NR-1 de saúde mental?",
                acceptedAnswer: { "@type": "Answer", text: "Sim. A atualização da NR-1 exige que toda empresa, de qualquer porte ou setor, implemente gestão de riscos psicossociais com diagnóstico (DRPS), plano de ação contínuo e registro no PGR. O descumprimento pode gerar autuações, multas e impacto no FAP." },
              },
              {
                "@type": "Question",
                name: "Como funciona o psicólogo in company da Equilybra?",
                acceptedAnswer: { "@type": "Answer", text: "Disponibilizamos psicólogos dentro da empresa em escala definida com o RH, oferecendo atendimento individual confidencial, suporte a emergências psicológicas, rodas de conversa, mediação de conflitos e relatórios gerenciais sem identificação dos colaboradores." },
              },
              {
                "@type": "Question",
                name: "Vocês emitem laudos psicológicos para admissão e funções de risco?",
                acceptedAnswer: { "@type": "Answer", text: "Sim. Emitimos laudos psicológicos para admissão, promoção, transferência, demissão, concursos, funções de risco e avaliações conforme NR-20, NR-33, NR-34, NR-35 e parâmetros ANAC. Usamos testes aprovados pelo CFP." },
              },
              {
                "@type": "Question",
                name: "A Equilybra atende empresas de qualquer porte?",
                acceptedAnswer: { "@type": "Answer", text: "Sim. Atendemos micro, pequenas, médias e grandes empresas em São Paulo e em todo o Brasil, com planos de saúde mental corporativa modulados por número de colaboradores, setor e maturidade do RH." },
              },
              {
                "@type": "Question",
                name: "Vocês oferecem palestras e treinamentos para lideranças?",
                acceptedAnswer: { "@type": "Answer", text: "Sim. Oferecemos palestras corporativas, treinamento de lideranças, SIPAT, workshops de gestão emocional, prevenção ao burnout, comunicação não violenta e capacitação de RH em saúde mental no trabalho." },
              },
              {
                "@type": "Question",
                name: "Como contratar os serviços corporativos da Equilybra Company?",
                acceptedAnswer: { "@type": "Answer", text: "Solicite uma proposta pelo WhatsApp (11) 97697-4905, pelo telefone (11) 2305-0747 ou pelo e-mail contato@equilybracompany.com.br. Fazemos um diagnóstico inicial gratuito e desenhamos um plano sob medida." },
              },
            ],
          },
        ]}
      />
      <HeroSection />
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <ScrollReveal variant="fade-up">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6 text-center">
              Psicologia Empresarial e Laudos Psicológicos em São Paulo
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5">
              A <strong>Equilybra Company</strong> é uma clínica de psicologia empresarial sediada em São Paulo — SP, especializada em
              soluções de saúde mental corporativa para empresas de todos os portes e segmentos. Atuamos como parceiros estratégicos
              de departamentos de Recursos Humanos, gestores de pessoas e lideranças que desejam construir ambientes de trabalho
              mais saudáveis, produtivos e humanos.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5">
              Nossa equipe é formada por psicólogos clínicos, peritos e neuropsicólogos com experiência consolidada em
              <strong> avaliação neuropsicológica</strong>, <strong>laudos psicológicos</strong>, <strong>perícia psicológica</strong>,
              <strong> atestado psicossocial</strong>, <strong>adequação à Norma Regulamentadora NR-1</strong> e programas de
              <strong> psicólogo in company</strong>. Oferecemos atendimento presencial em nossa unidade na capital paulista e
              também atendimento online sob consulta, garantindo flexibilidade para colaboradores e times distribuídos.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5">
              Com a atualização da NR-1, a gestão de riscos psicossociais passou a ser uma obrigação legal para todas as empresas
              brasileiras. A Equilybra Company desenvolve diagnósticos, planos de ação contínuos, treinamentos de liderança,
              rodas de conversa, atendimentos individuais e relatórios técnicos que mantêm sua organização em conformidade,
              ao mesmo tempo em que promovem engajamento, retenção de talentos e redução de afastamentos por transtornos mentais.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Também emitimos laudos especializados para cirurgia bariátrica, processos judiciais, avaliações ocupacionais e
              perícias técnicas. Investir em saúde mental é investir no futuro do seu negócio — fale com nossa equipe e descubra
              como podemos apoiar sua empresa.
            </p>
          </ScrollReveal>
        </div>
      </section>
      <Suspense fallback={<SectionFallback />}>
        <MissionVisionSection />
        <B2BSolutionsSection />
        <ServicesSection />
        <ClinicGallerySection />
        <SocialSection />
      </Suspense>
    </Layout>
  );
};

export default Index;
